
import { Options, Vue } from "vue-class-component";
import MultipleSelectSearch from "@/app/ui/components/multiple-select-search/index.vue";

type GroupBy = "daily" | "weekly" | "monthly";
interface RequestParams {
  start_date?: string;
  end_date?: string;
  comparison_start_date?: string;
  comparison_end_date?: string;
  group_by?: GroupBy;
  cod_types?: string;
  channels?: string;
  product_types?: string;
  destination_3lc?: string;
  commodity_codes?: string;
}

interface FilterOption {
  value: string;
  label: string;
}

@Options({
  components: {
    MultipleSelectSearch
  },
  props: {
    preserveOrder: {
      type: Boolean,
      default: false
    },
    filterLabel: {
      type: String,
      required: true
    },
    filterIcon: {
      type: String,
      required: true
    },
    filterIconActive: {
      type: String,
      required: true
    },
    ellipsisMaxItems: {
      type: Number,
      default: 3
    },
    showSearchInput: {
      default: true,
      type: Boolean
    },

    // MultipleSelectSearch props
    options: {
      type: Array as () => FilterOption[],
      required: true
    },
    selected: {
      type: Array as () => string[],
      default: () => []
    },
    searchable: {
      type: Boolean,
      default: false
    },
    useInfiniteScroll: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },

    // Event handlers
    onSearch: {
      type: (Function as unknown) as () => (search: string) => void,
      required: false
    },
    onScroll: {
      type: (Function as unknown) as () => () => void,
      required: false
    },
    onChange: {
      type: (Function as unknown) as () => (selected: string[]) => void,
      required: true
    },

    requestParamKey: {
      type: String,
      default: ""
    },
    baseRequestParams: {
      type: Object as () => RequestParams,
      default: () => ({})
    },
    onApply: {
      type: (Function as unknown) as () => (params: RequestParams) => void,
      required: false
    }
  }
})
export default class MultipleSelectPillSearch extends Vue {
  // --- reactive state
  private internalIsOpen = false;
  private pendingSelection: string[] = [];
  private appliedOrder: string[] = []; // order of values when user last hit Apply
  private filterContainerRef: HTMLElement | null = null;
  private searchText = "";
  private userHasSelectedButNotApplied = false;
  private handleApplyCheck = false;
  private debounceTimer: number | null = null;
  public preserveOrder!: boolean; 

  // -----------------------------
  // UI helpers / computed-like getters
  // -----------------------------
  public get filterButtonText(): string {
    const selected = (this as any).selected as string[];
    const labels = this.getSelectedLabels(Array.isArray(selected) ? selected : []);
    const label = (this as any).filterLabel || "";
    const ellipsisMax = (this as any).ellipsisMaxItems ?? 3;

    if (labels.length === 0) return label;
    const quoted = labels.map((l) => `‘${l}’`);
    return quoted.length <= ellipsisMax ? `${label} ${quoted.join(" ")}` : `${label} ${quoted.slice(0, ellipsisMax).join(" ")} ...`;
  }

  get filterButtonClass(): string {
    if (this.isFilterOpen) {
      return "bg-gray-lp-2900 border-2 border-red-lp-100 text-red-lp-100";
    }
    return this.hasSelection
      ? "bg-red-lp-2600 border-2 border-red-lp-100 text-red-lp-100 hover:bg-red-lp-2600"
      : "bg-gray-lp-2900 border-2 border-red-lp-900 text-black-lp-100 hover:border-red-lp-100 hover-bg-red-lp-2600";
  }

  mounted(): void {
    document.addEventListener("click", this.handleClickOutside);
  }

  beforeUnmount(): void {
    document.removeEventListener("click", this.handleClickOutside);
  }

  handleClickOutside(event: MouseEvent): void {
    if (!this.internalIsOpen) return;
    const target = event.target as Node | null;
    const container = this.filterContainerRef || (this.$refs.filterContainer as HTMLElement) || null;
    if (container && target && !container.contains(target)) {
      this.resetSearch();
      this.internalIsOpen = false;

      // If there are pending changes and dropdown closed by outside click, we keep pendingSelection
      // (onClose logic elsewhere handles emitting if needed)
    }
  }

  get hasSelection(): boolean {
    const selected = (this as any).selected as string[];
    return Array.isArray(selected) && selected.length > 0;
  }

  get isFilterOpen(): boolean {
    return this.internalIsOpen;
  }

  // For infinite scroll we keep selected on top as before; for static list we apply custom sorting
  get sortedOptions(): FilterOption[] {
    const opts = (this as any).options as FilterOption[];
    if (!Array.isArray(opts)) return [];

    if ((this as any).useInfiniteScroll) {
      const selectedSet = new Set(this.pendingSelection.length ? this.pendingSelection : (this as any).selected || []);
      const selectedOptions = opts.filter((o: FilterOption) => selectedSet.has(o.value));
      const otherOptions = opts.filter((o: FilterOption) => !selectedSet.has(o.value));
      return [...selectedOptions, ...otherOptions];
    }

    return [...opts].sort((a: FilterOption, b: FilterOption) => a.label.localeCompare(b.label));
  }

  get applyButtonClass(): string {
    return this.hasPendingChanges ? "bg-red-lp-100 hover:bg-red-lp-100" : "bg-gray-lp-600 cursor-not-allowed";
  }

  get applyTextClass(): string {
    return this.hasPendingChanges ? "text-red-lp-100" : "text-gray-lp-600";
  }

  get hasPendingChanges(): boolean {
    const selected = (this as any).selected as string[];
    if (!Array.isArray(selected) || !Array.isArray(this.pendingSelection)) return false;
    const sortedSelected = [...selected].sort();
    const sortedPending = [...this.pendingSelection].sort();
    return JSON.stringify(sortedPending) !== JSON.stringify(sortedSelected);
  }

  // -----------------------------
  // Core data transformation used by child component
  // -----------------------------
  get dataSelectItems() {
    const opts = (this as any).options as FilterOption[] | undefined;
    const currentSelection = this.isFilterOpen ? this.pendingSelection : (this as any).selected || [];
    const selectedSet = new Set(Array.isArray(currentSelection) ? currentSelection : []);

    const items = Array.isArray(opts)
      ? opts.map((o) => ({ name: o.label, value: o.value, isActive: selectedSet.has(o.value) }))
      : [];

    // DEBUG (left intentionally) — can be removed in production
    // console.log("dataSelectItems debug", { isFilterOpen: this.isFilterOpen, pendingSelection: this.pendingSelection, selected: (this as any).selected, searchText: this.searchText, appliedOrder: this.appliedOrder, handleApplyCheck: this.handleApplyCheck });

 

    // Sort priority logic:
    // 1) If not searching and there is an appliedOrder (user previously applied) => show those existing applied values first in their applied order
    // 2) Existing applied values (from appliedOrder) should be top even if user later toggles selections in pendingSelection (but newly toggled should NOT jump to top)
    // 3) Otherwise alphabetical (or search handled elsewhere)

    // ⚡ NEW ORDERING LOGIC WITH preserveOrder SUPPORT
const isSearching = this.searchText && this.searchText.trim().length > 0;

if (this.preserveOrder) {
  // Keep original parent order
  const appliedSet = new Set(this.appliedOrder);

  // Pinned keep original appliedOrder sequence
  const pinned = this.appliedOrder
    .map(v => items.find(i => i.value === v))
    .filter(Boolean) as typeof items;

  // Other items follow original order from parent (NO ALPHABETICAL SORT)
  const rest = items.filter(i => !appliedSet.has(i.value));

  return [...pinned, ...rest];
}

// -------------------------
// Default (existing behaviour)
// -------------------------
if (!isSearching && Array.isArray(this.appliedOrder) && this.appliedOrder.length > 0) {
  const order = new Map(this.appliedOrder.map((v, i) => [v, i]));

  items.sort((a, b) => {
    const aIdx = order.has(a.value) ? order.get(a.value)! : null;
    const bIdx = order.has(b.value) ? order.get(b.value)! : null;

    if (aIdx !== null && bIdx !== null) return aIdx - bIdx;
    if (aIdx !== null) return -1;
    if (bIdx !== null) return 1;

    // non-applied → alphabetical
    return a.name.localeCompare(b.name);
  });
} else {
  // default alphabetical
  items.sort((a, b) => a.name.localeCompare(b.name));
}
    return items;
  }

  get dataValueItems() {
    return this.dataSelectItems.filter((item: any) => item.isActive);
  }

  get optionsBySearchItems() {
    if (!this.internalIsOpen) return [];
    const q = this.searchText.trim().toLowerCase();
    if (!q) return [];
    return this.dataSelectItems.filter((item: any) => String(item.name).toLowerCase().includes(q));
  }

  // -----------------------------
  // Open/close/reset handlers
  // -----------------------------
  onOpenDropdown() {
    // NOTE: do NOT clear appliedOrder here — we want appliedOrder to represent last applied selection
    // Reset search and prepare pendingSelection from current `selected` prop
    this.searchText = "";
    const onSearch = (this as any).onSearch as (search: string) => void;
    if (typeof onSearch === "function") onSearch("");
  }

  handleFilterToggle(): void {
    this.internalIsOpen = !this.internalIsOpen;
    if (this.internalIsOpen) {
      this.onOpenDropdown();
      const selected = (this as any).selected as string[];
      this.pendingSelection = Array.isArray(selected) ? [...selected] : [];
      this.filterContainerRef = (this.$refs.filterContainer as HTMLElement) || null;
      this.searchText = "";
      const onSearch = (this as any).onSearch as (search: string) => void;
      if (typeof onSearch === "function") onSearch("");
    } else {
      // Closing: if user left pending changes, emit change so controller is aware (same as previous behaviour)
      if (this.hasPendingChanges) {
        const onChange = (this as any).onChange as (selected: string[]) => void;
        if (typeof onChange === "function") onChange(this.pendingSelection);
        this.$emit("change", this.pendingSelection);
      }
    }
  }

  handleClearSelection(): void {
    this.pendingSelection = [];
    this.appliedOrder = [];
    this.handleApplyCheck = false;
    const onChange = (this as any).onChange as (selected: string[]) => void;
    if (typeof onChange === "function") onChange([]);
    this.$emit("change", []);
  }

  public resetSearch(): void {
    this.searchText = "";
    const onSearch = (this as any).onSearch as (search: string) => void;
    if (typeof onSearch === "function") onSearch("");
  }

  handleFilterChange(selected: string[]): void {
    this.pendingSelection = Array.isArray(selected) ? [...selected] : [];
  }

  public handleApply(): void {
    // Apply pendingSelection as the new appliedOrder
    this.handleApplyCheck = true;
    this.appliedOrder = [...this.pendingSelection];
    this.userHasSelectedButNotApplied = false;

    // Emit change
    const onChange = (this as any).onChange as (selected: string[]) => void;
    if (typeof onChange === "function") onChange(this.pendingSelection);

    // Build params
    const key = (this as any).requestParamKey as keyof RequestParams | "";
    const base = (this as any).baseRequestParams as RequestParams | undefined;
    const params: RequestParams = { ...(base || {}) };

    if (key) {
      if (key === "group_by") {
        const validGroupByValues: GroupBy[] = ["daily", "weekly", "monthly"];
        const firstValid = this.pendingSelection.find((v) => validGroupByValues.includes(v as GroupBy)) as GroupBy | undefined;
        params[key] = firstValid;
      } else {
        params[key] = this.pendingSelection.join(",");
      }
    }

    const onApply = (this as any).onApply as (params: RequestParams) => void;
    if (typeof onApply === "function") onApply(params);

    this.$emit("apply-request", params);
    this.$emit("change", this.pendingSelection);

    // Close dropdown
    this.internalIsOpen = false;
  }

  handleReset(): void {
    const selected = (this as any).selected as string[];
    this.pendingSelection = Array.isArray(selected) ? [...selected] : [];
  }

  getSelectedLabels(values: string[]): string[] {
    const opts = (this as any).options as FilterOption[];
    if (!Array.isArray(opts)) return values.filter((v) => !!v);
    const labelMap = new Map<string, string>(opts.map((o) => [o.value, o.label]));
    return values
      .map((v) => labelMap.get(v) ?? v)
      .filter((l): l is string => typeof l === "string");
  }

  // -----------------------------
  // Child bridging handlers
  // -----------------------------
  handleOpenSelect = () => {
    this.internalIsOpen = true;
  };

  handleCloseSelect = () => {
    this.internalIsOpen = false;
  };

  // When user chooses/unchooses an item inside the child, update pendingSelection only.
  // IMPORTANT: do not mutate appliedOrder here — new choices should not jump to top until Apply.
  handleChooseSelect = (_name: string, value: string) => {
    const set = new Set(this.pendingSelection);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    this.pendingSelection = Array.from(set);
    this.userHasSelectedButNotApplied = true;
    this.handleApplyCheck = false;
  };

  handleSearchTyping = (value: string) => {
    this.handleApplyCheck = false;
    if (!(this as any).searchable) return;
    this.searchText = value;
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => {
      const onSearch = (this as any).onSearch as (search: string) => void;
      if (typeof onSearch === "function") onSearch(value);
    }, 300);
  };

  handleScroll = (evt: Event) => {
    if (!(this as any).useInfiniteScroll) return;
    if ((this as any).isLoading) return;
    const el = (evt?.target as HTMLElement) || null;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (!nearBottom) return;
    const onScroll = (this as any).onScroll as () => void;
    if (typeof onScroll === "function") onScroll();
  };

  get currentFilterIcon(): string {
    const active = (this as any).filterIconActive as string;
    const inactive = (this as any).filterIcon as string;
    return this.isFilterOpen || this.hasSelection ? active : inactive;
  }

  noop = () => undefined;
}
